import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import {
  GridInitialState,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridRootProps,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";

interface GridCustomToolbarProps {
  gridId: string;
  apiRef: any;
  defaultGridState?: GridInitialState;
  showGridSaver?: boolean;
}

const GridCustomToolbar: React.FC<GridCustomToolbarProps> = ({
  gridId,
  apiRef,
  defaultGridState,
  showGridSaver,
}) => {
  const [savedStates, setSavedStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string | null>("default");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [stateLabel, setStateLabel] = useState<string>("");
  const [showGridSaverUI, setShowGridSaverUI] = useState<boolean>(
    showGridSaver ?? true
  );
  const rootProps = useGridRootProps();

  useEffect(() => {
    const states = Object.keys(localStorage).filter((key) =>
      key.startsWith(gridId)
    );
    setSavedStates(states);
  }, [gridId]);

  useEffect(() => setShowGridSaverUI(showGridSaver ?? true), [showGridSaver]);

  const handleSaveState = () => {
    setDialogOpen(true);
  };

  const handleSaveStateConfirm = () => {
    const stateId = `${gridId}-${stateLabel}`;
    const state = apiRef.current.exportState();
    localStorage.setItem(stateId, JSON.stringify(state));
    setSavedStates([...savedStates, stateId]);
    setSelectedState(stateId); // Automatically set the newly saved state as selected
    setDialogOpen(false);
    setStateLabel("");
  };

  const handleLoadState = (event: SelectChangeEvent<string>) => {
    const stateId = event.target.value as string;
    setSelectedState(stateId);
    if (stateId === "default" && defaultGridState) {
      apiRef.current.restoreState(defaultGridState);
    } else {
      const savedState = localStorage.getItem(stateId);
      if (savedState) {
        apiRef.current.restoreState(JSON.parse(savedState));
      }
    }
  };

  const handleRemoveState = () => {
    if (selectedState && selectedState !== "default") {
      localStorage.removeItem(selectedState);
      setSavedStates(savedStates.filter((state) => state !== selectedState));
      setSelectedState("default");
      if (defaultGridState) {
        apiRef.current.restoreState(defaultGridState);
      }
    }
  };

  const handleUpdateState = () => {
    if (selectedState && selectedState !== "default") {
      const state = apiRef.current.exportState();
      localStorage.setItem(selectedState, JSON.stringify(state));
    }
  };

  return (
    <>
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        {showGridSaverUI == true && (
          <Box
            sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}
          >
            <FormControl
              variant="outlined"
              sx={{ minWidth: 200, marginRight: 2 }}
            >
              <InputLabel id="select-saved-state-label">Saved Views</InputLabel>
              <Select
                labelId="select-saved-state-label"
                value={selectedState ?? ""}
                onChange={handleLoadState}
                label="Saved Views"
                sx={{ height: "40px" }}
              >
                <MenuItem value="default">Default</MenuItem>
                {savedStates.map((stateId) => (
                  <MenuItem key={stateId} value={stateId}>
                    {stateId.replace(`${gridId}-`, "")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveState}
              {...rootProps.slotProps?.baseButton}
              sx={{
                backgroundColor: "var(--color-dark-blue)",
                color: "var(--color-light-gray)",
                "&:hover": {
                  backgroundColor: "var(--color-medium-blue)",
                },
              }}
            >
              Save Current View
            </Button>
            {selectedState && selectedState !== "default" && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleUpdateState}
                  sx={{
                    backgroundColor: "var(--color-dark-green)",
                    color: "var(--color-light-gray)",
                    marginLeft: 2,
                    "&:hover": {
                      backgroundColor: "var(--color-medium-blue)",
                    },
                  }}
                >
                  Update View
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleRemoveState}
                  sx={{
                    backgroundColor: "var(--color-medium-blue)",
                    color: "var(--color-light-gray)",
                    marginLeft: 2,
                    "&:hover": {
                      backgroundColor: "var(--color-dark-blue)",
                    },
                  }}
                >
                  Remove View
                </Button>
              </>
            )}
          </Box>
        )}
      </GridToolbarContainer>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Save Grid State</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a custom label for the current state.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Save As"
            type="text"
            fullWidth
            variant="outlined"
            value={stateLabel}
            onChange={(e) => setStateLabel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveStateConfirm} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GridCustomToolbar;
